import { assessmentNavigation } from './assessmentNavigation';
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import HomeDashboardLayout from '../HomeDashboardLayout';
import HeaderProfileSection from '../HeaderProfileSection';
import MyWalletSidebar from '../MyWalletSidebar';
import AssessmentSelectPlan from './AssessmentSelectPlan';
import AssessmentMySubscription from './AssessmentMySubscription';
import { getStoredUser } from '../../utils/authStorage';
import { useSidebarData } from '../../hooks/useSidebarData';
import { useStates } from '../../hooks/useStates';
import { getUserStateIdFromSidebar, getUserStateNameFromSidebar } from '../../questionBank/stateUtils';
import { createAssessmentItemOrder, fetchAssessmentPlans, fetchUserAssessmentPlans } from '../../services/assessmentSubscriptionService';
import { fetchCoinPaymentGateways } from '../../services/coinPurchaseService';
import { SUBSCRIPTION_ROUTES } from './subscriptionRoutes';
import { saveAssessmentDraft } from './assessmentDraft';
import '../../dashboard/Dashboard.css';
import '../HomeDashboard.css';
import '../gamification/Coinstore.css';
import '../questionBank/QuestionBankList.css';
import './AssessmentPlans.css';

const supportedSlugs = ['razorpay', 'bank-transfer', 'upi-transfer'];
const positiveId = (value) => Number(value) > 0 ? Number(value) : 0;

export default function AssessmentPlans() {
  const navigate = useNavigate();
  const location = useLocation();
  const purchaseContext = assessmentNavigation(location.state);
  const user = getStoredUser() || {};
  const { data: sidebarData } = useSidebarData();
  const statesQuery = useStates();
  const states = statesQuery.data?.states || [];
  const [selectedStateId, setSelectedStateId] = useState(null);
  const initialStateId = positiveId(location.state?.stateId || location.state?.states_id || location.state?.state_id || location.state?.selectedState?.state_id || getUserStateIdFromSidebar(sidebarData) || user.states_id || user.state_id);
  const defaultStateId = initialStateId || positiveId(states.find((item) => Number(item.is_user_state) === 1 && Number(item.is_disabled) !== 1)?.state_id);
  const selectedOptionId = selectedStateId ?? defaultStateId;
  const stateId = selectedOptionId === 0 ? positiveId(user.states_id) : selectedOptionId;
  const stateName = selectedOptionId === 0 ? '' : states.find((item) => Number(item.state_id) === selectedOptionId)?.state_name || (selectedOptionId === initialStateId ? location.state?.stateName || getUserStateNameFromSidebar(sidebarData) || user.state_name : '') || '';
  const quizId = positiveId(location.state?.quizId || location.state?.quiz_id);
  const seriesId = positiveId(location.state?.seriesId || location.state?.test_series_id);
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');
  const purchaseInProgress = useRef(false);
  const preparedOrders = useRef(new Map());
  const mySubscriptionRef = useRef(null);
  const waitingForDefaultState = statesQuery.isLoading && !initialStateId && selectedStateId === null;
  const plansQuery = useQuery({ queryKey: ['assessmentSubscriptions', stateId, quizId, seriesId], queryFn: () => fetchAssessmentPlans({ stateId, quizId, seriesId }), enabled: !waitingForDefaultState, retry: false, refetchOnWindowFocus: false });
  const mineQuery = useQuery({ queryKey: ['userAssessmentSubscriptions'], queryFn: fetchUserAssessmentPlans, retry: false, refetchOnWindowFocus: false });

  useEffect(() => { document.body.classList.add('subscription-ui-preview'); return () => document.body.classList.remove('subscription-ui-preview'); }, []);
  useEffect(() => {
    if (location.state?.tab === 'mySubscription' && !mineQuery.isLoading) mySubscriptionRef.current?.scrollIntoView?.({ block: 'start' });
  }, [location.state?.tab, mineQuery.isLoading]);

  const changeState = (nextStateId) => {
    setSelectedStateId(positiveId(nextStateId));
    preparedOrders.current.clear();
    setError('');
  };

  const subscribe = (plan, price) => {
    if (!(Number(plan.exam_quiz_plan_id) > 0 && Number(price?.duration_id) > 0 && Number(price?.value) > 0)) return;
    const draft = { ...purchaseContext, stateId, planId: Number(plan.exam_quiz_plan_id), durationId: Number(price.duration_id) };
    saveAssessmentDraft(draft);
    navigate(SUBSCRIPTION_ROUTES.assessmentDetail, { state: draft });
  };
  const unlock = async (kind, item) => {
    if (purchaseInProgress.current) return;
    purchaseInProgress.current = true;
    setBusyKey(`${kind}:${kind === 'quiz' ? item.quiz_id : item.test_series_id}`);
    setError('');
    try {
      const orderKey = `${kind}:${kind === 'quiz' ? item.quiz_id : item.test_series_id}:${item.discounted_price}`;
      const assessmentSubscriptionOrder = preparedOrders.current.get(orderKey) || await createAssessmentItemOrder(kind, item);
      preparedOrders.current.set(orderKey, assessmentSubscriptionOrder);
      const paymentGateways = (await fetchCoinPaymentGateways()).filter((gateway) => supportedSlugs.includes(gateway.slug));
      if (!paymentGateways.length) throw new Error('No supported web payment method is available.');
      const assessmentPurchase = {
        ...purchaseContext, kind, name: item.name, actual: Number(item.actual_price), discounted: Number(item.discounted_price), stateId,
        itemId: Number(kind === 'quiz' ? item.quiz_id : item.test_series_id),
      };
      navigate(SUBSCRIPTION_ROUTES.paymentMethod, { state: { assessmentPurchase, assessmentSubscriptionOrder, paymentGateways, profile: user } });
    } catch (failure) { setError(failure.message || 'Unable to start assessment checkout.'); }
    finally { purchaseInProgress.current = false; setBusyKey(''); }
  };

  return <><style>{`.homeDashboard-wrapper .page-content { padding-right: 0; } .homeDashboard-wrapper .main-content { box-sizing: border-box; padding-bottom: 35px; min-height: 0; height: calc(100vh - 44px) !important; } @media (max-width: 767px) { .homeDashboard-wrapper .main-content { height: 100vh !important; } }`}</style>
    <HomeDashboardLayout showHeader={false}><div className="qbl-page qbankList-page page-padding start-gamification"><div className="start-quiz qbl-main instruction-main">
      <div className="hideProfile-page"><div className="categories-page-header mb-0"><button type="button" className="categories-back-button" onClick={() => navigate(purchaseContext.assessmentReturnTarget?.pathname || SUBSCRIPTION_ROUTES.list, { state: purchaseContext.assessmentReturnTarget?.state })} aria-label={purchaseContext.assessmentReturnTarget ? "Back to quiz" : "Back to subscriptions"}><i className="bi bi-chevron-left" /></button><h4 className="d-flex categories-page-title"> </h4><HeaderProfileSection /></div></div>
      <div className="coine-balance-wrapper assessmentDetail"><div className="rapid-fire-header"><h1 className="qb-title">Assessment <span>Plan</span></h1></div><p className="game-description">Complete, Learn &amp; improve Daily</p>
        <div className="subject-checkout"><div className="subject-checkout__container">
          <AssessmentSelectPlan data={plansQuery.error ? {} : plansQuery.data} stateName={stateName} states={states} stateId={selectedOptionId} onStateChange={changeState} statesLoading={statesQuery.isLoading} statesError={statesQuery.error} onStatesRetry={statesQuery.refetch} plansLoading={waitingForDefaultState || (plansQuery.isLoading && !plansQuery.data)} plansError={plansQuery.error} onRetry={plansQuery.refetch} onSubscribe={subscribe} onUnlock={unlock} busyKey={busyKey} error={error} />
          <div ref={mySubscriptionRef}><AssessmentMySubscription subscriptions={mineQuery.data || []} loading={mineQuery.isLoading} error={mineQuery.error} onRetry={mineQuery.refetch} /></div>
        </div></div>
      </div>
    </div><MyWalletSidebar withTopOffset={false} /></div></HomeDashboardLayout>
  </>;
}
